<script setup>
import { ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';

// Etiqueta + valor editable en el sitio: se pulsa el valor, se escribe,
// Enter guarda y Esc cancela. Lo usan la tarjeta CONTACTO y la de
// seguimiento del panel de la conversación.
const props = defineProps({
  label: { type: String, required: true },
  value: { type: [String, Number], default: '' },
  type: { type: String, default: 'text' },
  placeholder: { type: String, default: '' },
  // Un valor derivado (la fecha de creación) se muestra pero no se edita.
  readonly: { type: Boolean, default: false },
  mono: { type: Boolean, default: false },
  isSaving: { type: Boolean, default: false },
});

const emit = defineEmits(['save']);

const { t } = useI18n();

const isEditing = ref(false);
const draft = ref('');
const inputRef = ref(null);

const startEdit = async () => {
  if (props.readonly) return;
  draft.value = props.value == null ? '' : String(props.value);
  isEditing.value = true;
  await nextTick();
  inputRef.value?.focus();
  inputRef.value?.select();
};

const cancel = () => {
  isEditing.value = false;
  draft.value = '';
};

const save = () => {
  if (!isEditing.value) return;
  const next = draft.value.trim();
  isEditing.value = false;
  if (next === String(props.value ?? '').trim()) return;
  emit('save', next);
};
</script>

<template>
  <div class="flex items-center gap-2.5 min-h-[22px] min-w-0 group/field">
    <span class="flex-shrink-0 w-[78px] text-[11.5px] text-n-slate-10">
      {{ label }}
    </span>
    <input
      v-if="isEditing"
      ref="inputRef"
      v-model="draft"
      :type="type"
      class="flex-1 min-w-0 h-[22px] px-1.5 rounded border border-n-brand/60 bg-n-solid-2 text-[12.5px] text-n-slate-12 outline-none reset-base"
      :class="{ 'font-mono text-[11.5px]': mono }"
      :placeholder="placeholder"
      :disabled="isSaving"
      @keydown.enter.prevent="save"
      @keydown.esc.prevent="cancel"
      @blur="save"
    />
    <span
      v-else-if="readonly"
      :title="value ? String(value) : undefined"
      class="text-[12.5px] font-medium truncate"
      :class="[
        value ? 'text-n-slate-12' : 'text-n-slate-9',
        mono ? 'font-mono text-[11.5px]' : '',
      ]"
    >
      {{ value || '—' }}
    </span>
    <button
      v-else
      type="button"
      class="flex items-center flex-1 min-w-0 gap-1.5 text-left rounded hover:bg-n-alpha-1 transition-colors"
      :title="value || placeholder || t('CONVERSATION_SIDEBAR.RAIL.EDIT')"
      @click="startEdit"
    >
      <span
        class="text-[12.5px] truncate"
        :class="[
          value ? 'font-medium text-n-slate-12' : 'text-n-slate-9',
          mono ? 'font-mono text-[11.5px]' : '',
        ]"
      >
        {{ value || '—' }}
      </span>
      <Icon
        icon="i-lucide-pencil"
        size="size-3 flex-shrink-0 text-n-slate-9 opacity-0 group-hover/field:opacity-100 transition-opacity"
      />
    </button>
  </div>
</template>
